import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Icon, Message, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import {
  CritiqueFeedbackVisibility,
  CritiqueGroup,
  CritiqueRevealPolicy,
  useDeleteCritiqueGroup,
  useUpdateFeedbackSettings,
} from 'src/hooks/useCritiqueGroups';
import GroupCoverPanel from 'src/components/critiqueGroups/groupCoverPanel';
import VisibilityPanel from './visibilityPanel';

/**
 * Design E1 — feedback visibility settings. Host (admin) only: the API rejects
 * a moderator here, so this tab is only rendered for the host.
 */

const Panel = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 14px;
  overflow: hidden;
  max-width: 620px;
`;

const Head = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid #eee7da;
  font-weight: 800;
  font-size: 17px;
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Foot = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #eee7da;
  background: var(--br-raised-surface);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

const GroupTitle = styled.div`
  font-weight: 800;
  font-size: 14px;
  margin-bottom: 3px;
`;

const GroupHelp = styled.div`
  font-size: 12.5px;
  color: #8a857b;
  margin-bottom: 12px;
`;

const Option = styled.div<{ $on: boolean; $disabled?: boolean }>`
  border: ${({ $on }) => ($on ? '2px solid #00b5ad' : '1.5px solid #e7e3d9')};
  background: ${({ $on }) => ($on ? '#f2fbfa' : '#fff')};
  border-radius: 12px;
  padding: 13px 15px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
`;

const Radio = styled.span<{ $on: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 4px solid ${({ $on }) => ($on ? '#00b5ad' : '#d0cabb')};
  margin-top: 2px;
  flex-shrink: 0;
`;

const DangerPanel = styled.div`
  background: var(--br-surface);
  border: 1px solid #f0d2cc;
  border-radius: 14px;
  overflow: hidden;
  max-width: 620px;
  margin-top: 20px;
`;

const DangerHead = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid #f6e0db;
  font-weight: 800;
  font-size: 17px;
  color: #b03a2e;
`;

const HostSettings: React.FC<{ group: CritiqueGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const save = useUpdateFeedbackSettings(group._id);
  const del = useDeleteCritiqueGroup();

  const [visibility, setVisibility] = useState<CritiqueFeedbackVisibility>(
    group.feedbackVisibility
  );
  const [reveal, setReveal] = useState<CritiqueRevealPolicy>(group.revealPolicy);
  const [saved, setSaved] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const confirmDelete = () =>
    del.mutate(
      { groupId: group._id },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          history.push('/critique-groups');
        },
      }
    );

  // Re-sync when the group refetches, so a change made elsewhere isn't
  // silently overwritten by stale local state.
  useEffect(() => {
    setVisibility(group.feedbackVisibility);
    setReveal(group.revealPolicy);
  }, [group.feedbackVisibility, group.revealPolicy]);

  const dirty =
    visibility !== group.feedbackVisibility || reveal !== group.revealPolicy;

  const submit = () => {
    setSaved(false);
    save.mutate(
      { feedbackVisibility: visibility, revealPolicy: reveal },
      { onSuccess: () => setSaved(true) }
    );
  };

  const sharedSelected = visibility === 'members_shared';

  return (
    <>
      <GroupCoverPanel group={group} />
      <VisibilityPanel group={group} />
      <Panel data-testid="host-settings">
      <Head>{t('CritiqueFeedbackVisibility', 'Feedback visibility')}</Head>
      <Body>
        <div>
          <GroupTitle>{t('CritiqueWhoSees', 'Who sees member feedback?')}</GroupTitle>
          <GroupHelp>
            {t(
              'CritiqueWhoSeesHelp',
              'Controls whether members can read each other’s comments & survey answers. You always see everything.'
            )}
          </GroupHelp>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Option
              $on={visibility === 'host_only'}
              onClick={() => setVisibility('host_only')}
              data-testid="settings-host-only"
            >
              <Radio $on={visibility === 'host_only'} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                  {t('CritiqueOnlyMe', 'Only me (the author)')}
                </div>
                <div style={{ fontSize: 12, color: '#8a857b', marginTop: 4 }}>
                  {t('CritiqueOnlyMeHelp', 'Feedback stays private to you.')}
                </div>
              </div>
            </Option>
            <Option
              $on={sharedSelected}
              onClick={() => setVisibility('members_shared')}
              data-testid="settings-shared"
            >
              <Radio $on={sharedSelected} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                  {t('CritiqueMembersSeeEachOther', 'Members see each other’s')}
                </div>
                <div style={{ fontSize: 12, color: '#6b675e', marginTop: 4 }}>
                  {t('CritiqueSharedHelp', 'A shared conversation.')}
                </div>
              </div>
            </Option>
          </div>
        </div>

        <div>
          <GroupTitle>{t('CritiqueWhenSee', 'When can they see it?')}</GroupTitle>
          <GroupHelp>
            {t(
              'CritiqueWhenSeeHelp',
              'Only applies when members can see each other’s feedback.'
            )}
          </GroupHelp>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Option
              $on={reveal === 'immediate'}
              $disabled={!sharedSelected}
              onClick={() => sharedSelected && setReveal('immediate')}
              data-testid="settings-immediate"
            >
              <Radio $on={reveal === 'immediate'} />
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                {t('CritiqueRevealImmediate', 'Immediately')}
              </div>
            </Option>
            <Option
              $on={reveal === 'after_own'}
              $disabled={!sharedSelected}
              onClick={() => sharedSelected && setReveal('after_own')}
              data-testid="settings-after-own"
            >
              <Radio $on={reveal === 'after_own'} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                  {t('CritiqueRevealAfterOwn', 'Only after they’ve shared their own')}
                </div>
                <div style={{ fontSize: 12, color: '#00817b', marginTop: 4 }}>
                  {t(
                    'CritiqueRevealAfterOwnHelp',
                    'Reduces anchoring — members form an honest first impression before seeing others.'
                  )}
                </div>
              </div>
            </Option>
          </div>
        </div>

        {save.isError && (
          <Message negative size="small" data-testid="settings-error">
            {save.error?.message}
          </Message>
        )}
      </Body>
      <Foot>
        {saved && !dirty && (
          <span style={{ fontSize: 12.5, color: '#2fa84f', fontWeight: 700 }}>
            {t('CritiqueSettingsSaved', 'Saved')}
          </span>
        )}
        <Button
          color="teal"
          onClick={submit}
          disabled={!dirty || save.isLoading}
          loading={save.isLoading}
          style={{ borderRadius: 9, fontWeight: 700 }}
          data-testid="settings-save"
        >
          {t('CritiqueSaveSettings', 'Save settings')}
        </Button>
      </Foot>
      </Panel>

      <DangerPanel data-testid="host-danger-zone">
        <DangerHead>{t('CritiqueDangerZone', 'Danger zone')}</DangerHead>
        <Body>
          <div>
            <GroupTitle>{t('CritiqueDeleteGroup', 'Delete this group')}</GroupTitle>
            <GroupHelp>
              {t(
                'CritiqueDeleteGroupHelp',
                'Permanently removes the group, its discussion and every membership, and revokes members’ access to the linked book. This can’t be undone.'
              )}
            </GroupHelp>
            {del.isError && (
              <Message negative size="small" data-testid="delete-error">
                {del.error?.message}
              </Message>
            )}
            <Button
              basic
              color="red"
              onClick={() => setConfirmOpen(true)}
              style={{ borderRadius: 9, fontWeight: 700 }}
              data-testid="delete-group-button"
            >
              <Icon name="trash" />
              {t('CritiqueDeleteGroup', 'Delete this group')}
            </Button>
          </div>
        </Body>
      </DangerPanel>

      <Modal
        open={confirmOpen}
        size="tiny"
        onClose={() => setConfirmOpen(false)}
        data-testid="delete-confirm-modal"
      >
        <Modal.Header>
          {t('CritiqueDeleteConfirmTitle', 'Delete “{{name}}”?', {
            name: group.name,
          })}
        </Modal.Header>
        <Modal.Content>
          {t(
            'CritiqueDeleteConfirmBody',
            'This permanently removes the group and everyone’s access to it. This can’t be undone.'
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setConfirmOpen(false)} data-testid="delete-cancel">
            {t('Cancel', 'Cancel')}
          </Button>
          <Button
            color="red"
            onClick={confirmDelete}
            loading={del.isLoading}
            disabled={del.isLoading}
            data-testid="delete-confirm"
          >
            {t('CritiqueDeleteConfirmYes', 'Delete group')}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default HostSettings;
